/**
 * AddSystemFieldModal — F12.M Etap C.3.
 *
 * Modal „Dodaj pole dodatkowe" do modułu systemowego.
 *
 * Różnice vs `AddFieldModal` (custom modules):
 *  - 12 typów whitelisted (`SYSTEM_FIELD_TYPES`) zamiast 48 z FieldRegistry
 *    Powód: ext-tabela `<code>_<entity>_ext` ma sensowne mapowanie PG-type tylko dla
 *    tych 12 (varchar/text/integer/numeric/boolean/date/timestamp/select/multiselect/
 *    email/url/reference). Reszta (richtext, signature, formula, rollup, ...) nie ma
 *    sensu jako kolumna w ext.
 *  - Brak selektora modułu (slug z URL, modal otwarty zawsze w kontekście jednego modułu)
 *  - Explicit slug input (slug = nazwa fizycznej kolumny w ext-tabeli, regex
 *    `[a-z][a-z0-9_]{0,62}`)
 *
 * Backend: POST `/api/system-modules/:slug/fields` (B.8).
 *
 * UWAGA (Etap D): w F12.M B endpoint zapisuje TYLKO metadata. Etap D doda
 * fizyczne `ALTER TABLE <code>_<entity>_ext ADD COLUMN`. Do tego czasu pole
 * istnieje w meta-tabeli, ale wartości nigdzie nie persystują.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SYSTEM_EXT_FIELD_TYPES, type SystemExtFieldType } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { DictionaryParamBindingsEditor } from '@/components/dictionaries/DictionaryParamBindingsEditor';
import { DictionaryPicker } from '@/components/dictionaries/DictionaryPicker';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import { type DictionaryParamBindingsMap, useDictionary } from '@/hooks/useDictionary';
import { api } from '@/lib/api';

/**
 * Whitelist 12 typów dla pól modułów systemowych (subset 48 z FieldRegistry).
 * Źródło prawdy: `packages/shared/src/field-types.ts`.
 * Re-eksportujemy pod starą nazwą dla kompatybilności z importerami.
 */
export const SYSTEM_FIELD_TYPES = SYSTEM_EXT_FIELD_TYPES;

/**
 * Typ literalny dla tokenów systemu ext-pól.
 * Re-eksportujemy pod starą nazwą dla kompatybilności z importerami
 * (ModuleFieldsTab.tsx, SystemModuleFieldsPanel.tsx, SystemModuleBuilderPage.tsx).
 */
export type SystemFieldType = SystemExtFieldType;

/** i18n key per typ. Format: `tenant.systemFieldTypes.<type>`. */
export function systemFieldTypeI18nKey(type: SystemFieldType): string {
  return `tenant.systemFieldTypes.${type}`;
}

const SLUG_REGEX = /^[a-z][a-z0-9_]{0,62}$/;

function resolveSlug(f: FormState): string {
  return f.slugTouched ? f.slug : nameToSlug(f.name);
}

function nameToSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 63) || 'pole'
  );
}

interface AddSystemFieldModalProps {
  open: boolean;
  onClose: () => void;
  moduleSlug: string;
}

/** Typy pola dla których pokazujemy picker słownika (opcje listy pochodzą ze słownika). */
const DICTIONARY_CAPABLE_TYPES: ReadonlySet<SystemFieldType> = new Set(['select', 'multiselect']);

interface FormState {
  name: string;
  slug: string;
  slugTouched: boolean;
  fieldType: SystemFieldType;
  description: string;
  isRequired: boolean;
  isUnique: boolean;
  isSearchable: boolean;
  isFilterable: boolean;
  showInList: boolean;
  dictionaryCode: string | null;
  /** Nadpisanie sposobu wyświetlania pozycji słownika dla TEGO pola (Etap 1c) —
   *  `null` = użyj `dictionary.displayMode` (fallback). Ma sens tylko gdy
   *  `dictionaryCode` jest ustawiony. */
  dictionaryDisplayMode: 'label' | 'code' | 'code_label' | null;
  /**
   * Wiązania parametrów słownika zewnętrznego (Etap 10.2/10.5, ADR-0066 §11; Faza 2b
   * ADR-0068 — tagowane wiązania: pole rekordu / firma / user / stała). `null` = brak
   * wiązań (słownik ręczny albo zewnętrzny bez parametrów).
   */
  dictionaryParamBindings: DictionaryParamBindingsMap | null;
  /**
   * Slug kartoteki docelowej relacji (F.REF v1) — ma sens tylko dla
   * `fieldType === 'reference'`. Kreator oferuje 6 opcji: 5 kartotek płaskich
   * (`contractors` domyślna, `employees`, `users`, `products`, `companies` — każda ma
   * `coreFieldMap` w adapterze) + `documents` (F20 — JEDEN generyczny token dla
   * dowolnej z 7 klas dokumentów, klasa wybierana w runtime wewnątrz
   * `RecordTablePicker`) — wszystkie wymagane przez `RecordTablePicker`, patrz
   * `lib/system-entity-adapters.ts`. `null` = brak wyboru (nie powinno się zdarzyć
   * w praktyce — dropdown auto-selectuje `contractors` przy przełączeniu typu na
   * `reference`, patrz `onChange` selecta `fieldType`).
   */
  relationSystemModuleSlug: string | null;
}

const INITIAL_FORM: FormState = {
  name: '',
  slug: '',
  slugTouched: false,
  fieldType: 'varchar',
  description: '',
  isRequired: false,
  isUnique: false,
  isSearchable: true,
  // Domyślnie WŁĄCZONE dla nowych pól (owner decision, smoke fix) — backend i tak
  // ma type-gate po stronie serwera (kolumny niefiltrowalne typu nie dostają filtra
  // mimo tej flagi), więc default `true` jest bezpieczny dla każdego typu pola.
  // Bez tego nowe pola dodatkowe cicho nie miały filtra na liście, a admin musiał o
  // tym pamiętać przy każdym dodaniu pola.
  isFilterable: true,
  showInList: true,
  dictionaryCode: null,
  dictionaryDisplayMode: null,
  dictionaryParamBindings: null,
  relationSystemModuleSlug: null,
};

/** Domyślny target oferowany w kreatorze (pierwsza kartoteka płaska), domyślnie wybrany. */
const REFERENCE_TARGET_CONTRACTORS = 'contractors';
/** Pozostałe 4 kartoteki płaskie — każda ma `coreFieldMap` w adapterze (`lib/system-entity-adapters.ts`). */
const REFERENCE_TARGET_EMPLOYEES = 'employees';
const REFERENCE_TARGET_USERS = 'users';
const REFERENCE_TARGET_PRODUCTS = 'products';
const REFERENCE_TARGET_COMPANIES = 'companies';
/** F20 — szósty target: JEDEN generyczny token dla dowolnej z 7 klas dokumentów (klasa
 *  wybierana w runtime wewnątrz `RecordTablePicker`, patrz `lib/system-entity-adapters.ts`). */
const REFERENCE_TARGET_DOCUMENTS = 'documents';
const REFERENCE_TARGET_TASKS = 'tasks';

export function AddSystemFieldModal({ open, onClose, moduleSlug }: AddSystemFieldModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setForm(INITIAL_FORM);
    }
  }, [open]);

  const effectiveSlug = resolveSlug(form);

  // Pola-sąsiedzi tego samego modułu (core + ext) — kandydaci do wiązania
  // parametrów `record_field` (Etap 10.5). Ten sam endpoint/queryKey co
  // `useKartotekaRenderField` (silnik formatek) — cache współdzielony.
  const siblingFieldsQuery = useQuery({
    queryKey: ['system-module-fields', moduleSlug],
    queryFn: async () => {
      const res = await api.get<{ fields: Array<{ id: string; slug: string; name: string }> }>(
        `/system-modules/${moduleSlug}/fields`,
      );
      return res.data.fields;
    },
    enabled: open,
    staleTime: 60_000,
  });

  // Szczegół wybranego słownika (params — filtr `record_field` w widgetcie).
  const dictionaryDetail = useDictionary(
    DICTIONARY_CAPABLE_TYPES.has(form.fieldType) && form.dictionaryCode ? form.dictionaryCode : '',
  );

  const createMutation = useMutation({
    mutationFn: async (input: FormState) => {
      const res = await api.post<{ field: { id: string; slug: string; name: string } }>(
        `/system-modules/${moduleSlug}/fields`,
        {
          name: input.name.trim(),
          slug: resolveSlug(input).trim(),
          fieldType: input.fieldType,
          description: input.description.trim() === '' ? null : input.description.trim(),
          isRequired: input.isRequired,
          isUnique: input.isUnique,
          isSearchable: input.isSearchable,
          isFilterable: input.isFilterable,
          showInList: input.showInList,
          dictionaryCode: DICTIONARY_CAPABLE_TYPES.has(input.fieldType)
            ? input.dictionaryCode
            : null,
          dictionaryDisplayMode:
            DICTIONARY_CAPABLE_TYPES.has(input.fieldType) && input.dictionaryCode
              ? input.dictionaryDisplayMode
              : null,
          dictionaryParamBindings:
            DICTIONARY_CAPABLE_TYPES.has(input.fieldType) && input.dictionaryCode
              ? input.dictionaryParamBindings
              : null,
          // Cel relacji tylko dla pola typu 'reference' — dla pozostałych typów nie
          // dokładamy tych kluczy do payloadu (mirror AddKartotekaFieldModal.tsx).
          // v1: zawsze single-select (kreator nie oferuje wyboru wielokrotności) —
          // patrz komentarz przy `relationMultiple` w `ExtFieldValueInputField`.
          ...(input.fieldType === 'reference'
            ? {
                relationSystemModuleSlug:
                  input.relationSystemModuleSlug ?? REFERENCE_TARGET_CONTRACTORS,
                relationMultiple: false,
              }
            : {}),
        },
      );
      return res.data.field;
    },
    onSuccess: async () => {
      toast.show({ variant: 'success', title: t('tenant.systemModuleFields.toasts.added') });
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['system-module', moduleSlug] }),
        // Bug 2026-07-14 (E.2): siblingFieldsQuery (edytor wiązań `record_field`) i
        // `useKartotekaRenderField` (silnik formatek) dzielą ten queryKey —
        // bez inwalidacji świeżo dodane pole nie pojawia się na liście pól-rodzeństwa
        // przy natychmiastowym ponownym otwarciu modala (staleTime 60s).
        queryClient.invalidateQueries({ queryKey: ['system-module-fields', moduleSlug] }),
      ]);
      onClose();
    },
    onError: (err) => {
      const message = isAxiosError(err)
        ? (err.response?.data?.error ?? t('tenant.systemModuleFields.toasts.addError'))
        : t('tenant.systemModuleFields.toasts.addError');
      toast.show({
        variant: 'error',
        title: t('tenant.systemModuleFields.toasts.addError'),
        description: String(message),
      });
    },
  });

  const slugValid = SLUG_REGEX.test(effectiveSlug);
  const canSubmit =
    form.name.trim().length > 0 &&
    slugValid &&
    !createMutation.isPending &&
    (form.fieldType !== 'reference' || !!form.relationSystemModuleSlug);

  const handleSubmit = () => {
    if (!canSubmit) return;
    createMutation.mutate(form);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenant.systemModuleFields.modal.title')}
      description={t('tenant.systemModuleFields.modal.description')}
      size="md"
    >
      <div className="space-y-4">
        <FormField label={t('tenant.systemModuleFields.modal.name')} required>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t('tenant.systemModuleFields.modal.namePlaceholder')}
            maxLength={100}
            autoFocus
            data-testid="add-system-field-name"
          />
        </FormField>

        <FormField
          label={t('tenant.systemModuleFields.modal.slug')}
          hint={
            slugValid
              ? t('tenant.systemModuleFields.modal.slugHint')
              : t('tenant.systemModuleFields.modal.slugInvalid')
          }
          required
        >
          <Input
            value={effectiveSlug}
            onChange={(e) => setForm({ ...form, slug: e.target.value, slugTouched: true })}
            placeholder="np. numer_krs"
            maxLength={63}
            data-testid="add-system-field-slug"
          />
        </FormField>

        <FormField label={t('tenant.systemModuleFields.modal.fieldType')} required>
          <Dropdown
            value={form.fieldType}
            onChange={(v) => {
              const nextType = v as SystemFieldType;
              setForm({
                ...form,
                fieldType: nextType,
                // Auto-select domyślnej opcji (contractors) przy przełączeniu na 'reference'
                // — zachowuje ręczny wybór (np. documents), gdy user wcześniej już
                // przełączał typ tam i z powrotem.
                relationSystemModuleSlug:
                  nextType === 'reference'
                    ? (form.relationSystemModuleSlug ?? REFERENCE_TARGET_CONTRACTORS)
                    : form.relationSystemModuleSlug,
              });
            }}
            options={SYSTEM_FIELD_TYPES.map((type) => ({
              value: type,
              label: t(systemFieldTypeI18nKey(type)),
            }))}
            data-testid="add-system-field-type"
          />
        </FormField>

        {form.fieldType === 'reference' && (
          <FormField label={t('tenant.systemModuleFields.modal.referenceTarget')} required>
            <Dropdown
              value={form.relationSystemModuleSlug ?? REFERENCE_TARGET_CONTRACTORS}
              onChange={(v) => setForm({ ...form, relationSystemModuleSlug: v })}
              options={[
                {
                  value: REFERENCE_TARGET_CONTRACTORS,
                  label: t('tenant.systemModuleFields.modal.referenceTargetContractors'),
                },
                {
                  value: REFERENCE_TARGET_EMPLOYEES,
                  label: t('tenant.systemModuleFields.modal.referenceTargetEmployees'),
                },
                {
                  value: REFERENCE_TARGET_USERS,
                  label: t('tenant.systemModuleFields.modal.referenceTargetUsers'),
                },
                {
                  value: REFERENCE_TARGET_PRODUCTS,
                  label: t('tenant.systemModuleFields.modal.referenceTargetProducts'),
                },
                {
                  value: REFERENCE_TARGET_COMPANIES,
                  label: t('tenant.systemModuleFields.modal.referenceTargetCompanies'),
                },
                {
                  value: REFERENCE_TARGET_DOCUMENTS,
                  label: t('tenant.systemModuleFields.modal.referenceTargetDocuments'),
                },
                {
                  value: REFERENCE_TARGET_TASKS,
                  label: t('tenant.systemModuleFields.modal.referenceTargetTasks'),
                },
              ]}
              data-testid="add-system-field-reference-target"
            />
          </FormField>
        )}

        {DICTIONARY_CAPABLE_TYPES.has(form.fieldType) && (
          <FormField label={t('common.dictionaryPicker.label')}>
            <DictionaryPicker
              value={form.dictionaryCode}
              onChange={(code) =>
                setForm({ ...form, dictionaryCode: code, dictionaryParamBindings: null })
              }
              data-testid="add-system-field-dictionary"
            />
          </FormField>
        )}

        {DICTIONARY_CAPABLE_TYPES.has(form.fieldType) && form.dictionaryCode && (
          <FormField label={t('common.dictionaryDisplayModeOverride.label')}>
            <Dropdown
              value={form.dictionaryDisplayMode ?? ''}
              onChange={(v) =>
                setForm({
                  ...form,
                  dictionaryDisplayMode: (v || null) as FormState['dictionaryDisplayMode'],
                })
              }
              options={[
                {
                  value: '',
                  label: t('common.dictionaryDisplayModeOverride.useDictionaryDefault'),
                },
                { value: 'label', label: t('common.dictionaryDisplayModeOverride.options.label') },
                { value: 'code', label: t('common.dictionaryDisplayModeOverride.options.code') },
                {
                  value: 'code_label',
                  label: t('common.dictionaryDisplayModeOverride.options.code_label'),
                },
              ]}
              data-testid="add-system-field-dictionary-display-mode"
            />
          </FormField>
        )}

        {DICTIONARY_CAPABLE_TYPES.has(form.fieldType) && form.dictionaryCode && (
          <DictionaryParamBindingsEditor
            params={dictionaryDetail.dictionary?.params}
            siblingFields={siblingFieldsQuery.data ?? []}
            value={form.dictionaryParamBindings}
            onChange={(bindings) => setForm({ ...form, dictionaryParamBindings: bindings })}
            data-testid="add-system-field-param-bindings"
          />
        )}

        <FormField label={t('tenant.systemModuleFields.modal.descriptionLabel')}>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            maxLength={500}
            data-testid="add-system-field-description"
          />
        </FormField>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <CheckboxRow
            label={t('tenant.systemModuleFields.modal.required')}
            checked={form.isRequired}
            onChange={(v) => setForm({ ...form, isRequired: v })}
            testId="add-system-field-required"
          />
          <CheckboxRow
            label={t('tenant.systemModuleFields.modal.unique')}
            checked={form.isUnique}
            onChange={(v) => setForm({ ...form, isUnique: v })}
            testId="add-system-field-unique"
          />
          <CheckboxRow
            label={t('tenant.systemModuleFields.modal.searchable')}
            checked={form.isSearchable}
            onChange={(v) => setForm({ ...form, isSearchable: v })}
            testId="add-system-field-searchable"
          />
          <CheckboxRow
            label={t('tenant.systemModuleFields.modal.filterable')}
            checked={form.isFilterable}
            onChange={(v) => setForm({ ...form, isFilterable: v })}
            testId="add-system-field-filterable"
          />
          <CheckboxRow
            label={t('tenant.systemModuleFields.modal.showInList')}
            checked={form.showInList}
            onChange={(v) => setForm({ ...form, showInList: v })}
            testId="add-system-field-show-in-list"
          />
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={createMutation.isPending}
          disabled={!canSubmit}
          data-testid="add-system-field-submit"
        >
          {t('tenant.systemModuleFields.modal.submit')}
        </Button>
      </div>
    </Modal>
  );
}

interface CheckboxRowProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  testId?: string;
}

function CheckboxRow({ label, checked, onChange, testId }: CheckboxRowProps) {
  return (
    <Checkbox
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      label={label}
      data-testid={testId}
    />
  );
}
